import { Button } from "@nimit9/signet-ui/components/button"
import { Checkbox } from "@nimit9/signet-ui/components/checkbox"
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@nimit9/signet-ui/components/combobox"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@nimit9/signet-ui/components/dialog"
import { Input } from "@nimit9/signet-ui/components/input"
import { Label } from "@nimit9/signet-ui/components/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@nimit9/signet-ui/components/select"
import { useState } from "react"
import toast from "react-hot-toast"
import { flattenCategories, useCategories } from "@/hooks/use-categories"
import { useCreateTransaction } from "@/hooks/use-create-transaction"
import { useOwnerLabels } from "@/hooks/use-owner-labels"
import { MAX_DESCRIPTION_LENGTH } from "@/lib/constants"
import { pickableCategories } from "@/lib/pickable-categories"

const TYPE_ITEMS = { debit: "Debit", credit: "Credit" } as const

export function AddTransactionDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const ownerLabels = useOwnerLabels()
  const { data: catData } = useCategories()
  const flatCats = catData ? flattenCategories(catData.data) : []
  const leafCats = pickableCategories(flatCats.filter((c) => !c.children?.length))

  const [form, setForm] = useState({
    date: new Date().toISOString().split("T")[0],
    amount: "",
    type: "debit" as "debit" | "credit",
    description: "",
    owner: "user_1",
    categoryId: "",
    referenceNumber: "",
    notes: "",
    isTransfer: false,
    isIgnored: false,
  })

  const resetForm = {
    date: new Date().toISOString().split("T")[0],
    amount: "",
    type: "debit" as const,
    description: "",
    owner: "user_1",
    categoryId: "",
    referenceNumber: "",
    notes: "",
    isTransfer: false,
    isIgnored: false,
  }

  const mutation = useCreateTransaction()

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.amount || !form.description) return
    mutation.mutate(
      {
        ...form,
        amount: parseFloat(form.amount).toFixed(2),
        categoryId: form.categoryId || undefined,
        referenceNumber: form.referenceNumber || undefined,
        notes: form.notes || undefined,
      },
      {
        onSuccess: (res) => {
          if ("offline" in res) toast("Saved offline — will sync when you're back online.")
          onClose()
          setForm(resetForm)
        },
      },
    )
  }

  function update<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add transaction</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Date + Type row */}
          <div className="flex gap-3">
            <div className="flex-1 space-y-1.5">
              <Label className="text-[11px] text-text-muted">Date</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => update("date", e.target.value)}
                required
              />
            </div>
            <div className="w-28 space-y-1.5">
              <Label className="text-[11px] text-text-muted">Type</Label>
              <Select
                value={form.type}
                onValueChange={(v) => update("type", v as "debit" | "credit")}
                items={TYPE_ITEMS}
              >
                <SelectTrigger size="sm">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="debit">Debit</SelectItem>
                  <SelectItem value="credit">Credit</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Amount */}
          <div className="space-y-1.5">
            <Label className="text-[11px] text-text-muted">
              Amount <span className="text-text-muted">*</span>
            </Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              value={form.amount}
              onChange={(e) => update("amount", e.target.value)}
              required
            />
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label className="text-[11px] text-text-muted">
              Description <span className="text-text-muted">*</span>
            </Label>
            <Input
              placeholder="What was this for?"
              value={form.description}
              maxLength={MAX_DESCRIPTION_LENGTH}
              onChange={(e) => update("description", e.target.value)}
              required
            />
          </div>

          {/* Category + Owner row */}
          <div className="flex gap-3">
            <div className="flex-1 space-y-1.5">
              <Label className="text-[11px] text-text-muted">Category</Label>
              <Combobox
                items={leafCats.map((c) => c.id)}
                itemToStringLabel={(id) => leafCats.find((c) => c.id === id)?.name ?? ""}
                value={form.categoryId || null}
                onValueChange={(id) => update("categoryId", id ?? "")}
              >
                <ComboboxInput placeholder="None" showClear={!!form.categoryId} className="h-7" />
                <ComboboxContent>
                  <ComboboxEmpty>No categories found</ComboboxEmpty>
                  <ComboboxList>
                    {(catId) => {
                      const cat = leafCats.find((c) => c.id === catId)
                      return (
                        <ComboboxItem key={catId} value={catId}>
                          {cat?.icon && <span className="mr-0.5">{cat.icon}</span>}
                          {cat?.name}
                        </ComboboxItem>
                      )
                    }}
                  </ComboboxList>
                </ComboboxContent>
              </Combobox>
            </div>
            <div className="w-32 space-y-1.5">
              <Label className="text-[11px] text-text-muted">Owner</Label>
              <Select
                value={form.owner}
                onValueChange={(v) => v && update("owner", v)}
                items={ownerLabels}
              >
                <SelectTrigger size="sm">
                  <SelectValue placeholder="Owner" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ownerLabels).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Reference + Notes */}
          <div className="space-y-1.5">
            <Label className="text-[11px] text-text-muted">Reference number</Label>
            <Input
              placeholder="Optional"
              value={form.referenceNumber}
              onChange={(e) => update("referenceNumber", e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] text-text-muted">Notes</Label>
            <Input
              placeholder="Optional"
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
            />
          </div>

          {/* Flags */}
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-[12px] text-text-muted cursor-pointer">
              <Checkbox
                checked={form.isTransfer}
                onCheckedChange={(v) => update("isTransfer", v === true)}
              />
              Transfer
            </label>
            <label className="flex items-center gap-2 text-[12px] text-text-muted cursor-pointer">
              <Checkbox
                checked={form.isIgnored}
                onCheckedChange={(v) => update("isIgnored", v === true)}
              />
              Ignored
            </label>
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending || !form.amount || !form.description}
            >
              {mutation.isPending ? "Adding…" : "Add transaction"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
